import { Icon } from "@iconify/react/dist/iconify.js";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  type ChatAttachment,
  deleteQuotationRequestChatAttachment,
} from "../../../../../api/quotation";

// Utility function to format bytes into human-readable format
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

interface AttachmentsListProps {
  chatSessionUid: string;
  attachments?: ChatAttachment[];
  refetchAttachments: () => void;
}

export function AttachmentsList({
  attachments,
  chatSessionUid,
  refetchAttachments,
}: AttachmentsListProps) {
  const deleteAttachmentMutation = useMutation({
    mutationFn: async ({ attachmentUid }: { attachmentUid: string }) => {
      return deleteQuotationRequestChatAttachment({
        chat_session_uid: chatSessionUid,
        attachment_uid: attachmentUid,
      });
    },
  });

  // Handle delete attachment success
  useEffect(() => {
    if (deleteAttachmentMutation.isSuccess) {
      toast.success("Attachment deleted successfully");
      deleteAttachmentMutation.reset();
      refetchAttachments();
    }
  }, [deleteAttachmentMutation.isSuccess, deleteAttachmentMutation.reset, refetchAttachments]);

  // Handle delete attachment error
  useEffect(() => {
    if (deleteAttachmentMutation.isError) {
      toast.error("Failed to delete attachment");
      deleteAttachmentMutation.reset();
    }
  }, [deleteAttachmentMutation.isError, deleteAttachmentMutation.reset]);

  return (
    <div className="grid gap-2 overflow-hidden grid-rows-[auto_1fr]">
      <h6 className="text-lg font-bold">Attachments</h6>
      <div className="overflow-y-auto p-4 space-y-4 border border-gray-200 rounded-md">
        {attachments?.map((attachment) => (
          <div
            key={attachment.uid}
            className="grid grid-cols-[auto_1fr_auto] items-center gap-2 bg-gray-100 rounded-md p-2"
          >
            <Icon icon="mdi:paperclip" className="w-5 h-5" />
            <p className="truncate" title={attachment.filename}>
              {attachment.filename}
              <br />
              {attachment.size && `(${formatFileSize(attachment.size)})`}
            </p>
            <button
              type="button"
              onClick={() => deleteAttachmentMutation.mutate({ attachmentUid: attachment.uid })}
              disabled={deleteAttachmentMutation.isLoading}
            >
              <Icon
                icon="mdi:close"
                className="w-4 h-4 text-red-500 hover:bg-red-500 hover:text-white rounded-full"
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
